import { CommonModule } from "@angular/common";
import { Component, inject, input, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from "@angular/router";
import { UtilService } from "@core/utils/util.service";
import { environment } from "@env/environment";
import { CreateTicketDialogComponent } from "@features/order-actions/components/create-ticket-dialog/create-ticket-dialog.component";
import { OrderCancelDialogComponent } from "@features/order-actions/components/order-cancel-dialog/order-cancel-dialog.component";
import { OrderRescheduleDialogComponent } from "@features/order-actions/components/order-reschedule-dialog/order-reschedule-dialog.component";
import {
  OrderActionBase,
  OrderCancelData,
} from "@features/order-actions/models/order-action.model";
import { OrderRexAriaService } from "@features/orders/services/order-rex-aria.service";
import { DialogManagerService } from "app/shared/services/dialog-manager.service";
import { TenantUrlUtilService } from "app/shared/services/tenant-url-util.service";
import { InvoiceDrawerManagerService } from "../../../invoices/services/invoice-drawer-manager";
import { OrderGroupResponse, OrderSummary } from "../../models/order.model";
import { OrderIntegrationServicesComponent } from "../order-integration-services/order-integration-services.component";
import { OrderInvoicingStatusComponent } from "../order-invoicing-status/order-invoicing-status.component";
import { OrderProductDialogComponent } from "../order-product-dialog/order-product-dialog.component";

@Component({
  selector: "app-order-accordion",
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    OrderInvoicingStatusComponent,
    OrderIntegrationServicesComponent,
  ],
  templateUrl: "./order-accordion.component.html",
  styleUrls: ["./order-accordion.component.scss"],
})
export class OrderAccordionComponent {
  isExpanded = input<boolean>(false);
  order = input.required<OrderGroupResponse>();
  tenantId = input.required<string | null>();

  @ViewChild(OrderInvoicingStatusComponent)
  invoicingStatus: OrderInvoicingStatusComponent;

  dialog = inject(MatDialog);

  router = inject(Router);

  activatedRoute = inject(ActivatedRoute);
  utilService = inject(UtilService);
  readonly orderRexAriaService = inject(OrderRexAriaService);
  dialogManagerService = inject(DialogManagerService);
  tenantUrlUtilService = inject(TenantUrlUtilService);
  invoiceDrawerManagerService = inject(InvoiceDrawerManagerService);

  shouldDisable(subOrder: OrderSummary, ...statuses: string[]): boolean {
    if (!subOrder || !subOrder.statusOrder || !statuses?.length) return false;
    const current = String(subOrder.statusOrder).toUpperCase();
    return statuses.some((s) => String(s ?? "").toUpperCase() === current);
  }

  formatSeller(seller: string): string {
    return seller.toLowerCase().replace(/\s+/g, "-");
  }

  redirectOrderGroupDetail(
    orderId?: string,
    dispatchNumber?: string,
    sectionDetail?: string,
  ) {
    // Si no se pasa orderId, usar el primero del grupo
    const targetOrderId = orderId || this.order().orders[0]?.orderId;

    if (!targetOrderId) {
      return;
    }

    let queryParams: any = { ...this.activatedRoute.snapshot.queryParams };
    queryParams[environment.nameTypeRedirectParamUrl] = "ORDER_GROUP";
    queryParams[environment.nameOrderIdParamUrl] = targetOrderId;
    queryParams[environment.nameDispatchNumberParamUrl] = dispatchNumber;
    queryParams[environment.nameSectionOrderDetailUrl] = sectionDetail;

    this.tenantUrlUtilService.navigateWithBackUrl(
      [
        "orders",
        this.order().orders[0].tenantId,
        targetOrderId,
        this.order().orders[0].orderType,
        "detail",
      ],
      { queryParams },
    );
  }

  getShippingEstimate(subOrder: OrderSummary): Date | undefined {
    return subOrder.statusOrder == "DELIVERED"
      ? subOrder.shippingEstimate
      : subOrder.deliveryDate;
  }

  getDeliveryDate(subOrder: OrderSummary): Date | undefined {
    return subOrder.statusOrder == "DELIVERED"
      ? subOrder.deliveryDate
      : undefined;
  }

  openOrderProductDrawer(event: MouseEvent) {
    const data: OrderActionBase = {
      tenantId: this.order().orders[0].tenantId,
      orderGroup: this.order().orderGroup,
      orderType: this.order().orders[0].orderType,
    };
    this.dialogManagerService.openDrawer(
      OrderProductDialogComponent,
      data,
      event,
    );
  }

  openCreateTicketDrawer(event: MouseEvent, order: OrderSummary) {
    const data: OrderActionBase = {
      orderId: order.orderId,
      orderType: order.orderType,
    };
    this.dialogManagerService.openDrawer(
      CreateTicketDialogComponent,
      data,
      event,
    );
  }

  openOrderRescheduleDrawer(event: MouseEvent, order: OrderSummary) {
    const data: OrderActionBase = {
      tenantId: order.tenantId,
      orderId: order.orderId,
      orderType: order.orderType,
    };
    this.dialogManagerService.openDrawer(
      OrderRescheduleDialogComponent,
      data,
      event,
    );
  }

  openOrderCancelDrawer(event: MouseEvent, order: OrderSummary) {
    const data: OrderCancelData = {
      tenantId: order.tenantId,
      orderId: order.orderId,
      saleNoteNumber: order.saleNoteNumber!,
      total: order.total,
      orderType: order.orderType,
      payment: {
        paymentMethod: order.paymentSystem,
        paymentDigitCard: order.paymentDigitCard,
      },
    };
    this.dialogManagerService.openDrawer(
      OrderCancelDialogComponent,
      data,
      event,
    );
  }

  openVoucherDialog(event: MouseEvent, order: OrderSummary) {
    event.preventDefault();
    event.stopPropagation();
    const data = {
      seller: order.seller,
      orderId: order.orderId,
      tenantId: order.tenantId,
      date: new Date(),
      email: this.order().email,
    };
    this.invoiceDrawerManagerService.openRecipentFromOrder(data);
  }
}
